import { useState } from "react"
import Badge from "@/components/Badge/Badge"
import CopyIcon from "@/components/CopyIcon/CopyIcon"
import DeckBrowser from "@/components/DeckBrowser/DeckBrowser"
import SeatRing from "@/components/SeatRing/SeatRing"
import SessionRules from "@/components/SessionRules/SessionRules"
import Spinner from "@/components/Spinner/Spinner"
import TextField from "@/components/TextField/TextField"
import LobbyPlan from "./LobbyPlan"
import { DECK_BROWSER_COPY } from "@/content/cardsRound"
import { UNTITLED_SESSION } from "@/content/customers"
import { LOBBY_COPY } from "@/content/lobby"
import { useDebouncedField } from "@/hooks/useDebouncedField"
import { useFirestoreWrite } from "@/hooks/useFirestoreWrite"
import { setReady, updateParticipant } from "@/services/participants"
import { useSessionStore } from "@/store/sessionStore"
import { selectAllReady, selectAmIReady, selectSession, selectSurface } from "@/store/selectors"
import type { FamilyNumber } from "@/theme/colors"
import { joinUrl } from "@/utils/routePaths"
import styles from "@/styles/pages/Lobby.module.css"

const COPIED_FOR_MS = 1800

/** The one write on this screen a button is waiting on — see `useFirestoreWrite`. */
const READY = "ready"

export default function Lobby() {
  const session = useSessionStore(selectSession)
  const participants = useSessionStore((s) => s.participants)
  const currentParticipantId = useSessionStore((s) => s.currentParticipantId)
  const surface = useSessionStore(selectSurface)
  const amIReady = useSessionStore(selectAmIReady)
  const allReady = useSessionStore(selectAllReady)
  const { write, isPending } = useFirestoreWrite()
  const [copied, setCopied] = useState(false)
  const [browsing, setBrowsing] = useState(false)
  const [editingSeat, setEditingSeat] = useState(false)
  const [showingPlan, setShowingPlan] = useState(false)

  const me = participants.find((p) => p.id === currentParticipantId) ?? null
  // The same family the seat ring gave this person — by position, not by name
  // hash, so the badge here and the one at the table never disagree.
  const meIndex = participants.findIndex((p) => p.id === currentParticipantId)
  const meFamily = meIndex >= 0 ? (((meIndex % 7) + 1) as FamilyNumber) : undefined

  // One field per detail, each saving only its own key — the same pattern the
  // customer details dialog uses.
  const save = (key: "name" | "jobRole") => (value: string) => {
    if (session && me) write(() => updateParticipant(session.id, me.id, { [key]: value }))
  }
  const name = useDebouncedField({ value: me?.name ?? "", onCommit: save("name") })
  const jobRole = useDebouncedField({ value: me?.jobRole ?? "", onCommit: save("jobRole") })

  if (!session) return null

  // The share link IS the session id, so there is nothing to look up and nothing
  // that can drift out of step with it.
  const link = joinUrl(session.id)
  const seated = participants.length

  const copy = () => {
    void navigator.clipboard?.writeText(link).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), COPIED_FOR_MS)
    })
  }

  /*
   * The deck is readable from the lobby by anyone, on purpose. A guest with five
   * minutes before the session starts reading the cards is a better-prepared
   * guest, and nothing in the lobby is picked yet.
   */
  const deckTeaser = (
    <button type="button" className={styles.deckTeaser} onClick={() => setBrowsing(true)}>
      <span className={styles.deckStack} aria-hidden="true">
        <span />
        <span />
      </span>
      {DECK_BROWSER_COPY.open} →
    </button>
  )

  const browser = browsing ? <DeckBrowser onClose={() => setBrowsing(false)} /> : null

  /*
   * A guest who has taken a seat gets the artifact's waiting room: the session's
   * name, the table filling up, and the one thing they can tell the facilitator.
   * `Session.tsx` drops the header and the bottom bar for it — nothing on this
   * screen is theirs to drive, so there is nothing for chrome to hold.
   *
   * Their name was typed on the join screen a moment ago, and the run sheet is
   * read-once, so both sit behind quiet disclosures rather than on the screen: a
   * reload walks straight past the join screen, and a typo has to stay fixable.
   */
  if (surface === "participant" && me) {
    return (
      <div className={styles.waitingRoom}>
        <p className={styles.waitingEyebrow}>{session.name || UNTITLED_SESSION}</p>
        <h1 className={styles.waitingTitle}>{LOBBY_COPY.lobbyTitle}</h1>

        <SeatRing
          participants={participants}
          meId={me.id}
          showEmptySeat
          centre={
            <>
              <p className={styles.ringCount}>{LOBBY_COPY.seated(seated)}</p>
              <p className={styles.ringStatus}>
                {allReady ? LOBBY_COPY.waitingLineAllIn : LOBBY_COPY.waitingForOthers}
              </p>
              {/*
               * Inside the ring, where the design puts it: it is the one thing
               * a guest can do, and out on its own below the table it read as a
               * footnote to the seats rather than as the point of the screen.
               *
               * A toggle, not a one-way commit: someone who says they are ready
               * and then reopens their details should be able to take it back,
               * and the facilitator watches the count rather than each press.
               */}
              <button
                type="button"
                className={amIReady ? `${styles.ready} ${styles.readyOn}` : styles.ready}
                aria-pressed={amIReady}
                aria-busy={isPending(READY) || undefined}
                onClick={() => write(() => setReady(session.id, me.id, !amIReady), READY)}
              >
                {isPending(READY) ? <Spinner size="sm" /> : null}
                {amIReady ? LOBBY_COPY.readyDone : LOBBY_COPY.readyCta}
              </button>
            </>
          }
        />

        <div className={styles.ctaRow}>{deckTeaser}</div>

        <div className={styles.fineRow}>
          <button
            type="button"
            className={styles.fine}
            aria-expanded={editingSeat}
            onClick={() => setEditingSeat(!editingSeat)}
          >
            {editingSeat ? LOBBY_COPY.editSeatOpen : LOBBY_COPY.editSeat}
          </button>
          <button
            type="button"
            className={styles.fine}
            aria-expanded={showingPlan}
            onClick={() => setShowingPlan(!showingPlan)}
          >
            {showingPlan ? LOBBY_COPY.hidePlan : LOBBY_COPY.planLabel}
          </button>
        </div>

        {editingSeat ? (
          <section className={styles.mine}>
            <p className={styles.eyebrow}>{LOBBY_COPY.meTitle}</p>
            <div className={styles.mineRow}>
              <Badge name={me.name} size={48} family={meFamily} />
              <div className={styles.mineFields}>
                <TextField
                  label={LOBBY_COPY.nameLabel}
                  placeholder={LOBBY_COPY.namePlaceholder}
                  strong
                  {...name}
                />
                <TextField
                  label={LOBBY_COPY.roleLabel}
                  placeholder={LOBBY_COPY.rolePlaceholder}
                  {...jobRole}
                />
              </div>
            </div>
          </section>
        ) : null}

        {showingPlan ? (
          <LobbyPlan config={session.config} customerName={session.customerName} />
        ) : null}

        {browser}
      </div>
    )
  }

  return (
    <div className={styles.lobby}>
      <div className={styles.table}>
        <section className={styles.intro}>
          <h1 className={styles.title}>{LOBBY_COPY.facTitle}</h1>
          <p className={styles.body}>{LOBBY_COPY.facBody}</p>
        </section>

        <SeatRing
          participants={participants}
          showEmptySeat
          centre={
            <>
              <p className={styles.ringStatus}>{LOBBY_COPY.waitingForOthers}</p>
              {/* Inside the ring: the link is what fills the table, so it
               * belongs at the middle of it rather than off to one side. */}
              <div className={styles.link}>
                <span className={styles.linkText}>{link.replace(/^https?:\/\//, "")}</span>
                <button
                  type="button"
                  className={styles.copy}
                  title={copied ? LOBBY_COPY.copiedLink : LOBBY_COPY.copyLink}
                  aria-label={copied ? LOBBY_COPY.copiedLink : LOBBY_COPY.copyLink}
                  onClick={copy}
                >
                  <CopyIcon done={copied} />
                </button>
              </div>
            </>
          }
        />

        <div className={styles.teaserRow}>{deckTeaser}</div>
      </div>

      {/* The run sheet is the facilitator's, and this half of the screen is only
       * ever theirs: everyone else in the lobby has a seat, and a seat returns
       * the waiting room above — where the plan sits behind its own disclosure.
       * Still guarded rather than assumed, because it is a panel of controls. */}
      {surface === "facilitator" ? <SessionRules /> : null}
      {browser}
    </div>
  )
}
